import React from 'react';
import PropTypes from 'prop-types';
import { FormattedDate, FormattedMessage, useIntl } from 'react-intl';
import dayjs from 'dayjs';

import {
    getFormattedNumber,
    TooltipAnchor,
} from '@ratehub/base-ui';
import { RATE_DETAILS_MESSAGES } from '@ratehub/mtg-common';


function ProductTableBestMortgageRatesMoreDetails({ regularPayment, lumpSum, rateHold, isPreApproval, ...otherProps }) {
    const intl = useIntl();

    return (
        <div {...otherProps}>
            <div className="detail-container rh-display-flex rh-flex-nowrap rh-align-items-center rh-text-s rh-my-0_25">
                <span className="rh-mr-0_5">
                    {intl.formatMessage(RATE_DETAILS_MESSAGES.PREPAYMENT_LABEL)}
                </span>
                <TooltipAnchor
                    className="details-tooltip rh-flex-shrink-0 rh-mr-0_5"
                    message={(
                        <FormattedMessage
                            id="web-components.ProductTableBestMortgageRatesTableMoreDetails.prepaymentTooltip"
                            defaultMessage="Prepayment options outline the flexibility you have to increase your monthly mortgage payments or make a lump sum payments against your mortgage balance."
                        />
                    )}
                    tooltipLocation="bottom"
                    nubbinAlignment="middle"
                    width="16em"
                />
                <span className="detail-value rh-ml-auto weight-medium">
                    {getFormattedNumber(regularPayment ? regularPayment / 100 : undefined, 'percent', intl)}
                </span>
            </div>

            <div className="detail-container rh-display-flex rh-flex-nowrap rh-align-items-center rh-text-s rh-my-0_25">
                <span className="rh-mr-0_5">
                    {intl.formatMessage(RATE_DETAILS_MESSAGES.LUMP_SUM_LABEL)}
                </span>
                <span className="detail-value rh-ml-auto weight-medium">
                    {getFormattedNumber(lumpSum ? lumpSum / 100 : undefined, 'percent', intl)}
                </span>
            </div>

            <div className="detail-container rh-display-flex rh-flex-nowrap rh-align-items-center rh-text-s rh-my-0_25">
                <span className="rh-mr-0_5">
                    {intl.formatMessage(RATE_DETAILS_MESSAGES.RATE_HOLD_LABEL)}
                </span>
                <TooltipAnchor
                    className="details-tooltip rh-flex-shrink-0 rh-mr-0_5"
                    message={(
                        <FormattedMessage
                            id="web-components.ProductTableBestMortgageRatesTableMoreDetails.rateHoldTooltip"
                            defaultMessage="The rate hold is the length of time the mortgage provider can hold the interest rate for you once approved. Submitting this form is the first step to getting your rate held, but there will be additional steps required depending on the mortgage lender."
                        />
                    )}
                    tooltipLocation="bottom"
                    nubbinAlignment="middle"
                    width="16em"
                />
                <span className="detail-value rh-ml-auto weight-medium">
                    <Choose>
                        <When condition={rateHold}>
                            <FormattedDate
                                value={dayjs().add(rateHold, 'days')}
                                month="long"
                                day="2-digit"
                            >
                                {formattedDate => (
                                    // due to timezone differences, we could have a hydration warning but we can safely ignore it
                                    <time suppressHydrationWarning>
                                        {formattedDate}
                                    </time>
                                )}
                            </FormattedDate>
                        </When>
                        <Otherwise>
                            {'-'}
                        </Otherwise>
                    </Choose>
                </span>
            </div>

            <div className="detail-container rh-display-flex rh-flex-nowrap rh-align-items-center rh-text-s rh-my-0_25">
                <span className="rh-mr-0_5">
                    {intl.formatMessage(RATE_DETAILS_MESSAGES.PREAPPROVAL_LABEL)}
                </span>
                <span className="detail-value rh-ml-auto weight-medium">
                    {intl.formatMessage(RATE_DETAILS_MESSAGES.PREAPPROVAL_VALUE, { isPreApproval: isPreApproval })}
                </span>
            </div>
        </div>
    );
}

ProductTableBestMortgageRatesMoreDetails.propTypes = {
    lumpSum: PropTypes.number,
    rateHold: PropTypes.number,
    regularPayment: PropTypes.number,
    isPreApproval: PropTypes.bool,
};


export default ProductTableBestMortgageRatesMoreDetails;
